import React, { useState, useEffect } from 'react';
import { Alert, Stack, Divider, OutlinedInput, InputAdornment, Grid2 as Grid, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import Main from '../../../layouts/Main';
import Container from '../../../components/Container';
import PageBreadCrumbs from '../../../components/PageBreadcrumbs';
import MiscReportContent from '../../../types/reports/miscReportContent';
import { useErrorBoundary } from 'react-error-boundary';
import { set, update } from 'lodash';
import ReportListExportSession from './ReportListExportSection';
import IndependentSpendingReportsTable from './IndependentSpendingReportsTable';
import BallotQuestionReport from '../../../types/reports/ballotQuestionReport';

const BallotQuestionSpendingReports = (): JSX.Element => {
  const [filerList, setFilerList] = useState([]);
  const [currentFiler, setCurrentFiler] = useState('All');
  const [ballotQuestionList, setBallotQuestionList] = useState<BallotQuestionReport[]>();
  const [currentBallotQuestion, setCurrentBallotQuestion] = useState('All');
  const [currentPosition, setCurrentPosition] = useState('');
  const [summary, setSummary] = useState('No reports found.');

  const [tableRows, setTableRows] = useState<MiscReportContent[]>();
  const [reports, setReports] = useState<MiscReportContent[]>();

  const { showBoundary } = useErrorBoundary();

  const dropDownYears = Array.from(new Array(10), (val, index) => new Date().getFullYear() % 2 === 0 ? new Date().getFullYear() : new Date().getFullYear() - 1 - index * 2);
  const [reportYear, setReportYear] = useState(() => {
    if (new Date().getFullYear() % 2 === 0) {
      return new Date().getMonth() === 0 ? new Date().getFullYear() - 2 : new Date().getFullYear();
    }

    return new Date().getFullYear() - 1;
  });

  const updateReportYear = (event: any) => {
    setReportYear(event.target.value);
    setCurrentBallotQuestion('All');
    setCurrentFiler('All');
    setCurrentPosition('All');
  };

  const updateFiler = (event: any) => {
    setCurrentFiler(event.target.value);
  }

  const updateBallotQuestion = (event: any) => {
    setCurrentBallotQuestion(event.target.value);
  }

  const updatePosition = (event: any) => {
    setCurrentPosition(event.target.value);
  }


  const getFilersDropdown = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/entities/${reportYear}?reportType=BQS`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the filers dropdown.'));
          }
          return result.json();
        })
        .then(result => {
          setFilerList(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getBallotQuestionDropdown = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/relatedbqs/${reportYear}/bqs`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the ballot questions dropdown.'));
          }
          return result.json();
        })
        .then(result => {
          console.log(result);
          setBallotQuestionList(result);
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }


  const getSummary = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/reports/summary/${reportYear}?reportTypeDescription=BQS`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the ballot question spending reports summary.'));
          }
          return result.json();
        })
        .then(result => {
          let count = result.count;
          if (count === 0) {
            setSummary("No reports found.");
            return;
          }
          else {
            setSummary(`Viewing 1 through ${count} of ${count} reports, ${result.totalDisplay}`);
          }


        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const getBallotQuestionSpendingReports = async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/miscreports/reports/${reportYear}?reportTypeDescription=BQS`)
        .then(result => {
          if (!result.ok) {
            showBoundary(new Error('Failed to get the ballot question spending reports.'));
          }
          return result.json();
        })
        .then(result => {
          setReports(result);
          setTableRows(result);
          getSummary();
        });
    } catch (error) {
      console.error('Uncaught Error:', error);
    }
  }

  const handleFilterChange = () => {
    const filtered = reports?.filter((row) => {
      const matchesFiler = currentFiler === 'All' || row.filingEntity.includes(currentFiler);
      const matchesBallotQuestion = currentBallotQuestion === 'All' || row.stateBqDescription.includes(currentBallotQuestion);
      const matchesPosition = currentPosition === 'All' || row.position.includes(currentPosition);
      return matchesFiler && matchesBallotQuestion && matchesPosition;
    });
    setTableRows(filtered);
    if (filtered) {
      filtered.length > 0 ? setSummary(`Viewing 1 through ${filtered.length} of ${reports?.length} reports`) : setSummary("No reports found.");
    }
  }


  useEffect(() => {
    getFilersDropdown();
    getBallotQuestionDropdown();
    getSummary();
    getBallotQuestionSpendingReports();
  }, [reportYear]);

  useEffect(() => {
    handleFilterChange();
  }, [currentFiler, currentBallotQuestion, currentPosition]);


  return (
    <Main>
      <Container>
        <PageBreadCrumbs></PageBreadCrumbs>
        <Typography
          variant="h5"
          align={'left'}
          data-aos={'fade-up'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={3}
          marginBottom={2}
        >
          Ballot Question Spending Reports
        </Typography>
        <Divider />
        <Grid container marginBottom={2} marginTop={3}>
          <Grid size={12} marginBottom={4}>
            <Typography marginBottom={1} sx={{ textIndent: 10 }} component={'p'} variant='body1'>In Massachusetts, if an individual or a for-profit business corporation, association, organization, or any other organized group of individuals (other than an organized political committee) gives, pays, expends or contributes, or promises to give, pay, expend or contribute money or any other thing of value to influence or affect the vote on any question submitted to the voters, the person or entity must file a report disclosing the amount or value of every gift, payment, expenditure or contribution or promise to give, pay, expend or contribute, together with the date, purpose and full name and address of the person to whom it was made. The requirement to file this report is set forth in M.G.L. C.55, s. 22, which is why these reports are referred to as "22 Reports." If the question appears on ballots at a state election, the report is filed with OCPF. Reports regarding questions that appear on ballots at a city or town election are filed with local election officials.</Typography>
            <Typography marginBottom={1} sx={{ textIndent: 10 }} component={'p'} variant='body1'>If a city, town, or other governmental unit spends resources to influence or affect the vote on a ballot question the treasurer of the governmental unit must file a similar disclosure report. If the question appears on a state ballot, the treasurer files the report with OCPF. If the question appears on the ballot for a city or town, the report is filed with local election officials. The requirement to file this report is set forth in M.G.L. c. 55, s. 22A, which is why these reports are referred to as "22A Reports."</Typography>
            <Typography marginBottom={1} sx={{ textIndent: 10 }} component={'p'} variant='body1'>Ballot question spending reports filed before September 1, 2012 were filed on paper. If you would like to view these reports, please visit the OCPF Archive.</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>

            <Stack direction={"row"} spacing={1}
              sx={{ mb: 1 }}
            >

              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="year-id">Year</InputLabel>
                <Select
                  sx={{ fontSize: 'smaller' }}
                  labelId="year-id"
                  id="year-select"
                  aria-labelledby='year-id'
                  label="Report Year"
                  value={reportYear}
                  onChange={updateReportYear}
                >
                  {
                    dropDownYears.map((year) =>
                      <MenuItem value={year} key={year} >{year}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="filer-id">Filer</InputLabel>
                <Select
                  sx={{ fontSize: 'smaller' }}
                  labelId="filer-id"
                  id="filer-select"
                  aria-labelledby='filer-id'
                  label="Report Year"
                  value={currentFiler}
                  onChange={updateFiler}
                >
                  <MenuItem value='All' key='All' >All</MenuItem>
                  {
                    filerList.map((year) =>
                      <MenuItem value={year} key={year} >{year}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="bq-id">Ballot Question</InputLabel>
                <Select
                  sx={{ fontSize: 'smaller' }}
                  labelId="bq-id"
                  id="bq-select"
                  aria-labelledby='bq-id'
                  label="Ballot Questions"
                  value={currentBallotQuestion}
                  onChange={updateBallotQuestion}
                >
                  <MenuItem value='All' key='All' >All</MenuItem>
                  {ballotQuestionList &&
                    ballotQuestionList.map((bq: any) =>
                      <MenuItem value={bq.text} key={bq.id} >{bq.text}</MenuItem>
                    )
                  }
                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <InputLabel id="position-id">Position</InputLabel>
                <Select
                  sx={{ fontSize: 'smaller' }}
                  labelId="position-id"
                  id="position-select"
                  aria-labelledby='position-id'
                  label="Position"
                  value={currentPosition}
                  onChange={updatePosition}
                >
                  <MenuItem value={'All'} key='all' >All</MenuItem>
                  <MenuItem value={'Support'} key='support' >Support</MenuItem>
                  <MenuItem value={'Oppose'} key='oppose' >Oppose</MenuItem>
                </Select>
              </FormControl>

            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            {/* <ReportListExportSession></ReportListExportSession> */}
          </Grid>
          <Grid size={12}>
            <Alert icon={false} severity="info"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '.MuiAlert-message': {
                  padding: '4px',
                }
              }}
            >
              {summary}
            </Alert>
          </Grid>
        </Grid>


        <IndependentSpendingReportsTable tableRows={tableRows} type='bqSpending' />
      </Container>
    </Main>
  );
};
export default BallotQuestionSpendingReports;